import {
  FlutterMessage,
  FlutterOutgoingMessageType,
  FlutterInitialData,
  FlutterIncomingMessageType,
} from "./flutterBridge.types";
import mockData from "./mockData.json";

const BRIDGE_TIMEOUT_MS = 5000;
const BRIDGE_CHECK_INTERVAL_MS = 100;

/** Check if running in local development mode */
const isLocalDevelopment = (): boolean => {
  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname;
  return hostname === "localhost" || hostname === "127.0.0.1";
};

/** Check if mock mode is enabled */
export const isMockMode = (): boolean => {
  return isLocalDevelopment() && !isFlutterBridgeAvailable();
};

/** Check if Flutter bridge is available */
export const isFlutterBridgeAvailable = (): boolean => {
  return (
    typeof window !== "undefined" &&
    typeof window.Flutter !== "undefined" &&
    typeof window.Flutter.postMessage === "function"
  );
};

/** Wait for Flutter bridge to be injected */
export const waitForFlutterBridge = (
  timeoutMs = BRIDGE_TIMEOUT_MS,
): Promise<boolean> => {
  return new Promise((resolve) => {
    // In local development, immediately resolve true to use mock data
    if (isLocalDevelopment()) {
      resolve(true);
      return;
    }

    if (isFlutterBridgeAvailable()) {
      resolve(true);
      return;
    }

    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (isFlutterBridgeAvailable()) {
        clearInterval(checkInterval);
        resolve(true);
      } else if (Date.now() - startTime >= timeoutMs) {
        clearInterval(checkInterval);
        resolve(false);
      }
    }, BRIDGE_CHECK_INTERVAL_MS);
  });
};

/** Send message to Flutter */
export const sendToFlutter = <T = unknown>(
  type: FlutterOutgoingMessageType | string,
  data?: T,
): void => {
  // In mock mode, don't actually send to Flutter
  if (isLocalDevelopment() && !isFlutterBridgeAvailable()) {
    return;
  }

  if (!isFlutterBridgeAvailable()) {
    return;
  }

  window.Flutter!.postMessage(type, data ?? {});
};

/** Register listener for Flutter messages */
export const onFlutterMessage = <T = unknown>(
  callback: (message: FlutterMessage<T>) => void,
): void => {
  if (!isFlutterBridgeAvailable()) {
    return;
  }

  window.Flutter!.onMessage((message) => {
    callback(message as FlutterMessage<T>);
  });
};

/** Initialize bridge and get initial data (token, settings) from Flutter */
export const initializeFlutterBridge = (): Promise<FlutterInitialData> => {
  return new Promise((resolve, reject) => {
    // In mock mode, return mock data immediately
    if (isLocalDevelopment() && !isFlutterBridgeAvailable()) {
      resolve(mockData.flutterInitialData as FlutterInitialData);
      return;
    }

    const timeout = setTimeout(() => {
      reject(new Error("Flutter bridge initialization timeout"));
    }, BRIDGE_TIMEOUT_MS);

    onFlutterMessage<FlutterInitialData>((message) => {
      if (
        message.type === FlutterIncomingMessageType.DATA_UPDATE &&
        message.data
      ) {
        clearTimeout(timeout);
        resolve(message.data);
      }
    });

    // Signal ready
    sendToFlutter(FlutterOutgoingMessageType.READY, {
      status: "initialized",
      timestamp: new Date().toISOString(),
    });

    // Request initial data (token, settings)
    sendToFlutter(FlutterOutgoingMessageType.REQUEST_DATA, {
      dataType: "appSettings",
    });
  });
};

/** Get mock data for local testing */
export const getMockData = () => mockData;

// Singleton: Track if Flutter listener is already registered
let isFlutterListenerRegistered = false;

/**
 * Initialize Flutter message forwarding to CustomEvents (singleton).
 * Only registers once, forwards all Flutter messages to CustomEvent system.
 */
const initFlutterMessageForwarding = (): void => {
  if (isFlutterListenerRegistered || typeof window === "undefined") return;

  if (isFlutterBridgeAvailable()) {
    window.Flutter!.onMessage((message) => {
      window.dispatchEvent(
        new CustomEvent("flutter-message", {
          detail: message,
        }),
      );
    });
    isFlutterListenerRegistered = true;
  }
};

/**
 * Dispatch a Flutter message internally (without Flutter actually sending it).
 * Useful for triggering the same behavior from web code.
 * Uses CustomEvent to notify all listeners.
 */
export const dispatchFlutterMessage = <T = unknown>(
  type: FlutterIncomingMessageType | string,
  data?: T,
): void => {
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent("flutter-message", {
      detail: { type, data },
    }),
  );
};

/**
 * Subscribe to Flutter messages (both real from Flutter and internal dispatches).
 * Returns an unsubscribe function for cleanup.
 */
export const subscribeToFlutterMessage = <T = unknown>(
  callback: (message: FlutterMessage<T>) => void,
): (() => void) => {
  // Ensure Flutter messages are forwarded to CustomEvent system (singleton)
  initFlutterMessageForwarding();

  // Listen for all messages via CustomEvent (both Flutter and internal)
  const handleCustomEvent = (event: Event) => {
    const customEvent = event as CustomEvent<FlutterMessage<T>>;
    callback(customEvent.detail);
  };

  window.addEventListener("flutter-message", handleCustomEvent);

  // Return unsubscribe function
  return () => {
    window.removeEventListener("flutter-message", handleCustomEvent);
  };
};
